// frontend/src/components/TransferDetailModal.js
/**
 * Read-only transfer detail modal. Follows design-system-reference.md § Modal System.
 * Layout aligned with AssetDetailModal: hero (identity + status), grouped Sections
 * (Transfer, Parties, Asset & condition), and conditional Rejection callout.
 * Portal, modal-overlay, modal-container-md, modal-header, modal-content, modal-footer.
 */

import React, { useState, useEffect } from 'react';
import { ArrowLeftRight, User, Package, AlertCircle, Image, Plus, Clock } from 'lucide-react';
import toast from '../utils/toast';
import Modal from './UI/Modal';
import { StatusBadge } from './UI/StatusBadge';
import { useTransfer, useAddTransferPhotos } from '../hooks/useAssetInventoryData';
import { formatDate } from '../utils/dateUtils';
import { getTransferFromParty, getTransferToParty } from '../utils/transferDisplayUtils';
import { resolveTransferTypeLabel } from '../utils/transferTypeMeta';
import { formatAssetTagForDisplay } from '../utils/mobileLineDisplay';
import { ReturnPhotoUploader, validateReturnPhotosForSubmit } from './asset/PendingReturnsShared';
import SecurePhotoThumb from './asset/SecurePhotoThumb';

// Align with AssetDetailModal: label + value, 2-col grid inside Section
const DetailField = ({ label, value, className = '' }) => (
  <div className={className}>
    <dt className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900">{value || '—'}</dd>
  </div>
);

const Section = ({ title, icon: Icon, children, className = '' }) => (
  <div className={`bg-gray-50 rounded-xl p-3 ${className}`}>
    <h4 className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {title}
    </h4>
    <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
      {children}
    </dl>
  </div>
);

// Photo type chip styling
const getPhotoTypeChip = (photoType, photoTypeDisplay) => {
  if (!photoType || !photoTypeDisplay) {
    return (
      <span className="inline-flex items-center px-2.5 py-1 bg-gray-50 text-gray-700 text-xs font-medium rounded-full">
        Unknown
      </span>
    );
  }

  // Color coding based on photo type
  let bgColor, textColor;
  if (photoType === 'return') {
    bgColor = 'bg-blue-50';
    textColor = 'text-blue-700';
  } else if (photoType === 'transfer_received') {
    bgColor = 'bg-green-50';
    textColor = 'text-green-700';
  } else if (photoType === 'assignment') {
    bgColor = 'bg-purple-50';
    textColor = 'text-purple-700';
  } else if (photoType === 'condition') {
    bgColor = 'bg-amber-50';
    textColor = 'text-amber-700';
  } else {
    // Fallback for other types
    bgColor = 'bg-gray-50';
    textColor = 'text-gray-700';
  }

  return (
    <span className={`inline-flex items-center px-2.5 py-1 ${bgColor} ${textColor} text-xs font-medium rounded-full`}>
      {photoTypeDisplay}
    </span>
  );
};

// True when a photo was uploaded on a later calendar day than the transfer
// itself — i.e. added as supporting evidence after the fact, not captured at
// the moment custody changed. Used to badge it so reviewers aren't misled.
const wasAddedAfterTransfer = (photo, transferDate) => {
  if (!photo?.uploaded_at || !transferDate) return false;
  const uploaded = new Date(photo.uploaded_at);
  const transferred = new Date(transferDate);
  if (Number.isNaN(uploaded.getTime()) || Number.isNaN(transferred.getTime())) return false;
  // Compare by day to avoid flagging same-session uploads minutes apart.
  uploaded.setHours(0, 0, 0, 0);
  transferred.setHours(0, 0, 0, 0);
  return uploaded.getTime() > transferred.getTime();
};

// Transaction Photos Section — authenticated thumbs via SecurePhotoThumb
const TransactionPhotosSection = ({ photos = [], transferDate = null, theme = 'admin' }) => {
  if (!photos || photos.length === 0) {
    return (
      <div className="bg-gray-50 rounded-xl p-3">
        <h4 className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          <Image className="w-3.5 h-3.5" />
          Transaction Photos
        </h4>
        <p className="text-xs text-gray-400 text-center py-2">
          No transaction photos available.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 rounded-xl p-3">
      <h4 className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        <Image className="w-3.5 h-3.5" />
        Transaction Photos
        <span className="ml-auto text-xs font-normal text-gray-400 normal-case">
          {photos.length} photo{photos.length !== 1 ? 's' : ''}
        </span>
      </h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {photos.map((photo) => (
          <SecurePhotoThumb
            key={photo.id}
            photoUrl={photo.photo_url || photo.photo}
            alt={photo.caption || 'Transaction photo'}
            theme={theme}
          >
            <div className="absolute top-2 left-2 z-10 flex flex-col items-start gap-1">
              {getPhotoTypeChip(photo.photo_type, photo.photo_type_display)}
              {wasAddedAfterTransfer(photo, transferDate) && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-medium rounded-full">
                  <Clock className="w-2.5 h-2.5" />
                  Added later
                </span>
              )}
            </div>
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-80 group-hover:opacity-100 transition-opacity">
              <p className="text-xs text-white truncate">
                {photo.uploaded_by_name || 'Unknown'}
              </p>
              <p className="text-xs text-white/70">
                {photo.uploaded_at ? formatDate(photo.uploaded_at) : ''}
              </p>
            </div>
          </SecurePhotoThumb>
        ))}
      </div>
    </div>
  );
};

const TransferDetailModal = ({ isOpen, transfer: initialTransfer, onClose, theme = 'admin', canAddPhotos = false }) => {
  // Fetch full transfer details when modal opens (includes photos)
  const transferId = initialTransfer?.id;
  const { data: fetchedTransfer, isLoading: loadingTransfer } = useTransfer(
    transferId,
    { enabled: isOpen && !!transferId }
  );
  
  // Use fetched transfer if available (has photos), otherwise fall back to initial
  const transfer = fetchedTransfer || initialTransfer;

  // Post-completion supporting-photo upload (custodian/HR only). Append-only.
  const addPhotos = useAddTransferPhotos();
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState([]);

  useEffect(() => {
    if (!isOpen) {
      setShowPhotoUpload(false);
      setPendingPhotos([]);
    }
  }, [isOpen]);

  const handleAddPhotos = () => {
    const photoError = validateReturnPhotosForSubmit(pendingPhotos);
    if (photoError) {
      toast.error(photoError);
      return;
    }
    if (pendingPhotos.length === 0) {
      toast.error('Select at least one photo to add.');
      return;
    }
    addPhotos.mutate(
      { transferId, photos: pendingPhotos },
      {
        onSuccess: () => {
          setPendingPhotos([]);
          setShowPhotoUpload(false);
        },
      }
    );
  };

  const focusRing = theme === 'admin' ? 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500' : 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';
  const closeClass = theme === 'admin' ? 'btn btn-admin-outline' : 'btn btn-outline';
  const iconBg = theme === 'admin' ? 'bg-red-100' : 'bg-blue-100';
  const iconColor = theme === 'admin' ? 'text-red-600' : 'text-blue-600';

  const typeLabel = resolveTransferTypeLabel(transfer);
  const dateLabel = ['return', 'offboarding'].includes(transfer?.transfer_type)
    ? 'Date of return'
    : 'Date';
  const isPending = transfer?.status === 'pending_acceptance' || transfer?.status === 'pending_receipt';
  const effectiveDateLabel = formatDate(transfer?.effective_date);
  const recordedDateLabel = formatDate(transfer?.transfer_date);
  const showRecordedDate = Boolean(
    transfer?.transfer_date
    && transfer?.effective_date
    && recordedDateLabel !== effectiveDateLabel
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Transfer Details"
      size="md"
      theme={theme}
      contentClassName="space-y-3"
      footer={
        <button
          type="button"
          onClick={onClose}
          className={`${closeClass} ${focusRing}`}
        >
          Close
        </button>
      }
    >
            {/* Identity + status compact banner */}
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 ${iconBg} rounded-full flex items-center justify-center flex-shrink-0`}>
                <ArrowLeftRight className={`w-4 h-4 ${iconColor}`} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-sm font-semibold text-gray-900 font-mono truncate">{transfer?.transfer_reference}</h2>
                  <StatusBadge status={transfer?.status} label={transfer?.status_display} />
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {formatAssetTagForDisplay(transfer?.asset_tag)}
                  {typeLabel && ` · ${typeLabel}`}
                  {transfer?.effective_date && ` · ${formatDate(transfer.effective_date)}`}
                  {isPending && transfer?.expires_at && ` · Expires ${formatDate(transfer.expires_at)}`}
                </p>
              </div>
            </div>

            {/* Section: Transfer */}
            <Section title="Transfer" icon={ArrowLeftRight}>
              <DetailField label="Type" value={typeLabel} />
              <DetailField label={dateLabel} value={effectiveDateLabel} />
              {showRecordedDate && (
                <DetailField label="Recorded" value={recordedDateLabel} />
              )}
              {isPending && <DetailField label="Expires" value={formatDate(transfer?.expires_at)} />}
            </Section>

            {/* Section: Parties */}
            <Section title="Parties" icon={User}>
              <DetailField label="From" value={getTransferFromParty(transfer)} />
              <DetailField label="To" value={getTransferToParty(transfer)} />
            </Section>

            {/* Section: Asset & condition */}
            <Section title="Asset & condition" icon={Package}>
              <DetailField label="Asset" value={formatAssetTagForDisplay(transfer?.asset_tag)} />
              <DetailField label="Asset type" value={transfer?.asset_type_name} />
              <DetailField label="Condition" value={transfer?.condition_at_transfer_display} />
              {transfer?.notes && (
                <div className="col-span-2">
                  <dt className="text-xs text-gray-500 font-medium uppercase tracking-wide">Notes</dt>
                  <dd className="mt-1 text-sm text-gray-700 whitespace-pre-wrap">{transfer.notes}</dd>
                </div>
              )}
            </Section>

            {/* Rejection callout (only when present) */}
            {transfer?.rejection_reason && (
              <div className="rounded-xl p-3 bg-red-50 border border-red-200 flex gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-xs font-semibold text-red-800 uppercase tracking-wide">Rejection reason</h4>
                  <p className="text-sm text-red-700 mt-0.5">{transfer.rejection_reason}</p>
                </div>
              </div>
            )}

            {/* Transaction Photos Section */}
            {loadingTransfer ? (
              <div className="bg-gray-50 rounded-xl p-3">
                <h4 className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  <Image className="w-3.5 h-3.5" />
                  Transaction Photos
                </h4>
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-xs text-gray-500">Loading photos...</span>
                </div>
              </div>
            ) : (
              <TransactionPhotosSection
                photos={transfer?.photos || []}
                transferDate={transfer?.transfer_date}
                theme={theme}
              />
            )}

            {/* Custodian/HR only: append supporting photos to a completed record */}
            {canAddPhotos && !loadingTransfer && transfer?.status === 'completed' && (
              <div className="rounded-xl border border-dashed border-gray-300 p-3">
                {!showPhotoUpload ? (
                  <button
                    type="button"
                    onClick={() => setShowPhotoUpload(true)}
                    className="w-full flex items-center justify-center gap-2 text-sm font-medium text-gray-600 hover:text-red-600 py-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add supporting photos
                  </button>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs text-gray-500">
                      Photos added now are recorded as supporting evidence and badged with the upload date.
                      Existing photos are never changed.
                    </p>
                    <ReturnPhotoUploader
                      photos={pendingPhotos}
                      onChange={setPendingPhotos}
                      label="New Photos"
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => { setShowPhotoUpload(false); setPendingPhotos([]); }}
                        className="btn btn-admin-outline btn-sm"
                        disabled={addPhotos.isPending}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleAddPhotos}
                        className="btn btn-admin-primary btn-sm"
                        disabled={addPhotos.isPending || pendingPhotos.length === 0}
                      >
                        {addPhotos.isPending ? 'Uploading...' : 'Upload Photos'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
    </Modal>
  );
};

export default TransferDetailModal;
